import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import RichTextEditor from "@/components/RichTextEditor";
import { Plus, ChevronDown, MapPin, MessageSquare, X, Pencil, Save, Search, RotateCcw } from "lucide-react";

const REPORT_TYPES = [
  { value: "facility_issue", label: "Facility issue", description: "Repairs, maintenance, or service problems.", requiresLocation: true, priority: "Medium" },
  { value: "safety_incident", label: "Safety incident", description: "An incident, hazard, or near miss that needs attention.", requiresLocation: true, priority: "High" },
  { value: "concern", label: "Concern", description: "Raise a personal, wellbeing, or conduct concern.", requiresLocation: false, priority: "Low" },
  { value: "suggestion_feedback", label: "Suggestion or feedback", description: "Share an idea or feedback about hostel life.", requiresLocation: false, priority: "Low" },
];
const CATEGORIES_BY_TYPE = {
  facility_issue: ["Plumbing", "Electrical", "Furniture", "Food", "Other"],
  safety_incident: ["Safety", "Near miss", "Other"],
  concern: ["Wellbeing concern", "Conduct concern", "Other"],
  suggestion_feedback: ["Suggestion", "Positive feedback", "Other"],
};
const PRIOS = ["Low", "Medium", "High", "Urgent"];
const PRIORITY_FILTERS = ["All", ...PRIOS];
const STATUSES = ["Open", "In Progress", "Completed"];
const ALL_CATEGORIES = "All categories";
const DEFAULT_PRIORITY_DEFINITIONS = `
<p><strong>Priority guide</strong></p>
<ol>
  <li><strong>Low</strong>: minor issue, no immediate risk to safety or service.</li>
  <li><strong>Medium</strong>: issue requires attention but does not disrupt daily operations.</li>
  <li><strong>High</strong>: urgent issue affecting comfort, functionality, or safety that needs attention soon.</li>
  <li><strong>Urgent</strong>: immediate risk, emergency, or severe disruption requiring rapid action.</li>
</ol>
`;

const prioColor = {
  Low: "bg-slate-100 text-slate-700",
  Medium: "bg-sky-100 text-sky-700",
  High: "bg-amber-100 text-amber-700",
  Urgent: "bg-red-100 text-red-700",
};
const statusColor = {
  Open: "bg-red-100 text-red-700",
  "In Progress": "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700",
  Resolved: "bg-emerald-100 text-emerald-700",
};

const formatCommentTimestamp = (value) => {
  if (!value) return "";
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(timestamp);
};

const reportTypeForIncident = (item) => item.report_type
  || (item.category === "Safety" ? "safety_incident" : item.category === "Suggestion" ? "suggestion_feedback" : "facility_issue");

export default function Incidents() {
  const { user } = useAuth();
  const isVisitor = user?.role === "visitor" || user?.staff_role === "Visitor";
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
  const [searchFilter, setSearchFilter] = useState("");
  const [priorityDefinitions, setPriorityDefinitions] = useState(DEFAULT_PRIORITY_DEFINITIONS);
  const [priorityDefinitionsDraft, setPriorityDefinitionsDraft] = useState(DEFAULT_PRIORITY_DEFINITIONS);
  const [priorityDefinitionsEditing, setPriorityDefinitionsEditing] = useState(false);
  const [savingPriorityDefinitions, setSavingPriorityDefinitions] = useState(false);
  const [form, setForm] = useState({ report_type: "facility_issue", category: "Plumbing", location: "", title: "", description: "", priority: "Medium", image: null, reporter_name: "", reporter_phone: "" });
  const [commentText, setCommentText] = useState({});
  const [resolutionDrafts, setResolutionDrafts] = useState({});
  const canManagePriorityDefinitions = user?.role === "admin" || user?.staff_role === "Hostel Supervisor";
  const selectedReportType = REPORT_TYPES.find((type) => type.value === form.report_type) || REPORT_TYPES[0];
  const availableCategories = [...new Set([...Object.values(CATEGORIES_BY_TYPE).flat(), ...items.map((item) => item.category).filter(Boolean)])];

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const load = useCallback(async () => {
    if (isVisitor) {
      setItems([]);
      return;
    }
    try {
      const [res, definitionRes] = await Promise.all([
        api.get("/incidents"),
        api.get("/incident-priority-definitions"),
      ]);
      setItems(res.data);
      setResolutionDrafts(Object.fromEntries(res.data.map((item) => [item.id, {
        mitigation: item.mitigation || "",
        root_cause: item.root_cause || "",
        solution: item.solution || "",
      }])));
      setPriorityDefinitions(definitionRes.data?.content || DEFAULT_PRIORITY_DEFINITIONS);
      setPriorityDefinitionsDraft(definitionRes.data?.content || DEFAULT_PRIORITY_DEFINITIONS);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error(error.response?.data?.detail || "Unable to load reports");
      }
      setItems([]);
    }
  }, [isVisitor]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (selectedReportType.requiresLocation && !form.location.trim()) return toast.error("Location required for this report type");
    if (isVisitor) {
      if (!form.reporter_name.trim() || !form.reporter_phone.trim()) {
        return toast.error("Please provide your name and phone number");
      }
    }
    await api.post("/incidents", form);
    setForm({ report_type: "facility_issue", category: "Plumbing", location: "", title: "", description: "", priority: "Medium", image: null, reporter_name: "", reporter_phone: "" });
    toast.success("Report submitted!");
    load();
  };

  const update = async (id, payload) => {
    const normalized = { ...payload };
    if (normalized.status === "Resolved") normalized.status = "Completed";
    if (normalized.assigned_person === undefined && normalized.technician !== undefined) {
      normalized.assigned_person = normalized.technician;
    }
    await api.put(`/incidents/${id}`, normalized);
    if (payload.comment) setCommentText({ ...commentText, [id]: "" });
    toast.success("Ticket updated");
    load();
  };

  const savePriorityDefinitions = async () => {
    setSavingPriorityDefinitions(true);
    try {
      const response = await api.put("/incident-priority-definitions", { content: priorityDefinitionsDraft });
      setPriorityDefinitions(response.data.content);
      setPriorityDefinitionsDraft(response.data.content);
      setPriorityDefinitionsEditing(false);
      toast.success("Priority definitions updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update priority definitions");
    } finally {
      setSavingPriorityDefinitions(false);
    }
  };

  const statusMatches = (item, filter) => filter === "All"
    || (filter === "Completed" ? ["Completed", "Resolved", "Closed"].includes(item.status) : item.status === filter);
  const filtered = items.filter((item) => {
    const query = searchFilter.trim().toLowerCase();
    const searchableText = [item.title, item.location, item.description, item.category, item.author_name]
      .filter(Boolean).join(" ").toLowerCase();
    return statusMatches(item, statusFilter)
      && (priorityFilter === "All" || item.priority === priorityFilter)
      && (categoryFilter === ALL_CATEGORIES || item.category === categoryFilter)
      && (!query || searchableText.includes(query));
  });
  const countForStatus = (status) => items.filter((item) => statusMatches(item, status)).length;
  const hasActiveFilters = statusFilter !== "All" || priorityFilter !== "All" || categoryFilter !== ALL_CATEGORIES || searchFilter.trim();
  const clearFilters = () => {
    setStatusFilter("All");
    setPriorityFilter("All");
    setCategoryFilter(ALL_CATEGORIES);
    setSearchFilter("");
  };

  return (
    <div className="space-y-8" data-testid="incidents-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Feedback & Incident Reports</p>
          <h1 className="mt-1 max-w-4xl font-display text-3xl font-extrabold">Feedback &amp; Report an Issue</h1>
          {isVisitor && (
            <p className="text-muted-foreground mt-1">Submit a new report. Previous reports are not visible to visitor accounts.</p>
          )}
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button data-testid="incident-create-btn"><Plus className="h-4 w-4 mr-1" /> New Report</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New {selectedReportType.label.toLowerCase()} report</DialogTitle><DialogDescription>{selectedReportType.description}</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Report type</Label>
                <Select value={form.report_type} onValueChange={(value) => {
                  const nextType = REPORT_TYPES.find((type) => type.value === value) || REPORT_TYPES[0];
                  setForm((current) => ({ ...current, report_type: value, category: CATEGORIES_BY_TYPE[value][0], priority: nextType.priority }));
                }}>
                  <SelectTrigger data-testid="incident-report-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REPORT_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className={`grid gap-3 ${selectedReportType.requiresLocation ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="incident-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES_BY_TYPE[form.report_type].map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {selectedReportType.requiresLocation && (
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="incident-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIOS.map((priority) => <SelectItem key={priority} value={priority}>{priority}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {isVisitor && (
                <>
                  <div className="space-y-2"><Label>Your name</Label><Input value={form.reporter_name} onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} placeholder="Enter your full name" data-testid="incident-reporter-name-input" /></div>
                  <div className="space-y-2"><Label>Your phone number</Label><Input value={form.reporter_phone} onChange={(e) => setForm({ ...form, reporter_phone: e.target.value })} placeholder="e.g. 0123456789" data-testid="incident-reporter-phone-input" /></div>
                </>
              )}
              <div className="space-y-2"><Label>{selectedReportType.requiresLocation ? "Block / Room" : "Location (optional)"}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder={selectedReportType.requiresLocation ? "e.g. Block B, Room 204" : "Add a location if relevant"} data-testid="incident-location-input" /></div>
              <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short summary" data-testid="incident-title-input" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue in detail…" data-testid="incident-description-input" /></div>
              <div className="space-y-2">
                <Label>Photo (optional)</Label>
                <Input type="file" accept="image/*" onChange={handleImage} data-testid="incident-image-input" />
                {form.image && (
                  <div className="relative mt-2 inline-block">
                    <img src={form.image} alt="Attachment preview" className="h-28 w-28 rounded-lg object-cover border border-border" data-testid="incident-image-preview" />
                    <button type="button" onClick={() => setForm({ ...form, image: null })} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center" data-testid="incident-image-remove" aria-label="Remove photo">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="incident-submit-btn">Submit report</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!isVisitor && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList data-testid="incident-filter-tabs">
                {["All", ...STATUSES].map((s) => (
                  <TabsTrigger key={s} value={s} data-testid={`filter-${s.replace(/\s/g, "-").toLowerCase()}`}>{s} <span className="ml-1 text-xs text-muted-foreground">{countForStatus(s)}</span></TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[min(100%,16rem)] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={searchFilter} onChange={(event) => setSearchFilter(event.target.value)} placeholder="Search reports" aria-label="Search reports" className="pl-9" data-testid="incident-search-input" />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-9 min-w-[150px]" aria-label="Filter by category" data-testid="incident-category-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_CATEGORIES}>{ALL_CATEGORIES}</SelectItem>
                  {availableCategories.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}
                </SelectContent>
              </Select>
              <div>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="h-9 min-w-[150px]" aria-label="Filter by priority" data-testid="incident-priority-filter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_FILTERS.map((value) => (
                      <SelectItem key={value} value={value}>{value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {hasActiveFilters && <Button variant="outline" size="sm" onClick={clearFilters} data-testid="incident-clear-filters"><RotateCcw className="mr-1 h-4 w-4" />Clear filters</Button>}
              <p className="w-full text-right text-xs text-muted-foreground" aria-live="polite" data-testid="incident-results-count">Showing {filtered.length} of {items.length} reports</p>
            </div>
          </div>

          <section className="rounded-2xl border border-border border-t-4 border-amber-500 bg-card p-4 sm:p-5" data-testid="incident-priority-definitions-section">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Priority definitions</p>
                <h2 className="mt-1 font-display text-xl font-bold">Response guide</h2>
              </div>
              {canManagePriorityDefinitions && (
                priorityDefinitionsEditing ? (
                  <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => { setPriorityDefinitionsDraft(priorityDefinitions); setPriorityDefinitionsEditing(false); }}>
                      Cancel
                    </Button>
                    <Button onClick={savePriorityDefinitions} disabled={savingPriorityDefinitions}>
                      <Save className="mr-1 h-4 w-4" /> {savingPriorityDefinitions ? "Saving…" : "Save"}
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => { setPriorityDefinitionsDraft(priorityDefinitions); setPriorityDefinitionsEditing(true); }}>
                    <Pencil className="mr-1 h-4 w-4" /> Edit definitions
                  </Button>
                )
              )}
            </div>
            {priorityDefinitionsEditing ? (
              <div className="mt-4 space-y-3">
                <RichTextEditor value={priorityDefinitionsDraft} onChange={setPriorityDefinitionsDraft} ariaLabel="Priority definitions editor" />
                <p className="text-xs text-muted-foreground">Use bold, underline, font sizing, bullets, and numbered lists to explain each priority level.</p>
              </div>
            ) : (
              <div className="mt-4 [&_ol]:list-decimal [&_ul]:list-disc [&_li]:ml-6 [&_strong]:font-semibold" dangerouslySetInnerHTML={{ __html: priorityDefinitions }} />
            )}
          </section>
        </div>
      )}

      {!isVisitor && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t-4 border-sky-500 pt-4">
          {filtered.length === 0 && (
              <div className="md:col-span-2 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
              No reports match these filters.
            </div>
          )}
          {filtered.map((it) => (
          <Collapsible key={it.id} className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm" data-testid={`incident-card-${it.id}`}>
            <CollapsibleTrigger className="group w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  {it.image && <img src={it.image} alt="Report attachment" className="h-14 w-14 shrink-0 rounded-lg border border-border object-cover" data-testid={`incident-image-${it.id}`} />}
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{REPORT_TYPES.find((type) => type.value === reportTypeForIncident(it))?.label || "Facility issue"} · {it.category}</p>
                    <h3 className="font-semibold leading-tight">{it.title}</h3>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge className={`${prioColor[it.priority] || prioColor.Medium} border-0`} data-testid={`incident-priority-${it.id}`}>{it.priority || "Medium"}</Badge>
                  <Badge className={`${statusColor[it.status] || statusColor.Completed} border-0`} data-testid={`incident-status-${it.id}`}>{it.status}</Badge>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {it.location}</span>
                <span>Opened {it.open_duration || "0m"}</span>
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent className="pt-4">
              {user?.role === "admin" && (
                <div className="mb-3 flex justify-end">
                  <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/incidents/${it.id}`); toast.success("Report deleted"); load(); }} aria-label="Delete report"><X className="h-4 w-4" /></Button>
                </div>
              )}
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{it.description}</p>
              {it.image && (
                <a href={it.image} target="_blank" rel="noopener noreferrer" className="block mt-3">
                  <img src={it.image} alt="Report attachment" className="rounded-lg max-h-56 w-full object-cover border border-border" />
                </a>
              )}
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {[
                  { field: "mitigation", label: "Mitigation / workaround" },
                  { field: "root_cause", label: "Root cause" },
                  { field: "solution", label: "Solution" },
                ].map(({ field, label }) => (
                  <div key={field} className="space-y-1.5">
                    <Label htmlFor={`incident-${field}-${it.id}`}>{label}</Label>
                    <Textarea
                      id={`incident-${field}-${it.id}`}
                      rows={2}
                      maxLength={5000}
                      value={resolutionDrafts[it.id]?.[field] ?? it[field] ?? ""}
                      onChange={(event) => setResolutionDrafts((current) => ({
                        ...current,
                        [it.id]: { ...(current[it.id] || {}), [field]: event.target.value },
                      }))}
                      placeholder={`Record ${label.toLowerCase()}…`}
                      data-testid={`incident-${field}-input-${it.id}`}
                    />
                  </div>
                ))}
                <div className="flex items-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => update(it.id, resolutionDrafts[it.id] || { mitigation: "", root_cause: "", solution: "" })}
                    data-testid={`incident-resolution-save-${it.id}`}
                  >
                    <Save className="mr-1 h-4 w-4" /> Save investigation details
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground flex-wrap">
                <span>by {it.author_name}</span>
                {(it.assigned_person || it.technician) && <span>Assigned to {it.assigned_person || it.technician}</span>}
              </div>

              {it.comments?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {it.comments.map((c) => (
                    <div key={c.id} className="text-sm bg-secondary rounded-lg px-3 py-2">
                      <span className="font-semibold">{c.author_name}: </span>{c.text}
                      {formatCommentTimestamp(c.created_at) && <time className="ml-2 text-xs text-muted-foreground" dateTime={c.created_at}>{formatCommentTimestamp(c.created_at)}</time>}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 flex gap-2">
                <Input value={commentText[it.id] || ""} onChange={(e) => setCommentText({ ...commentText, [it.id]: e.target.value })} placeholder="Add a comment…" className="h-9" data-testid={`incident-comment-input-${it.id}`} />
                <Button size="sm" variant="secondary" onClick={() => commentText[it.id]?.trim() && update(it.id, { comment: commentText[it.id] })} data-testid={`incident-comment-btn-${it.id}`}>
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <Select value={it.status} onValueChange={(v) => update(it.id, { status: v })}>
                  <SelectTrigger className="h-9" data-testid={`incident-status-select-${it.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input defaultValue={it.assigned_person || it.technician || ""} placeholder="Assign person" className="h-9" onBlur={(e) => {
                  const next = e.target.value.trim();
                  const current = (it.assigned_person || it.technician || "").trim();
                  if (next !== current) update(it.id, { assigned_person: next });
                }} data-testid={`incident-technician-input-${it.id}`} />
              </div>
            </CollapsibleContent>
          </Collapsible>
          ))}
        </div>
      )}
    </div>
  );
}
